import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Button, Portal, tokens } from "@fluentui/react-components";
import {
  ChevronDoubleLeft20Regular,
  ChevronDoubleRight20Regular,
  ChevronDown12Regular,
  Dismiss20Regular,
  HandPoint20Regular,
} from "@fluentui/react-icons";
import { helpText } from "./strings";
import {
  groupStepsBySection,
  localizedSection,
  localizedStep,
  type HelpLanguage,
  type HelpStep,
  type PageHelp,
} from "./types";
import { findStepElement, HELP_PANEL_ATTR } from "./dom";
import { revealHelpStep, revealRecipe } from "./reveal";
import { cardPosition } from "./position";
import { HelpStepJump } from "./HelpStepJump";
import { Z } from "./ui";

interface Props {
  page: PageHelp;
  /** Already filtered: permitted for the viewer AND on screen when the tour started. */
  steps: HelpStep[];
  /** The open dialog the tour is scoped to, or null for the whole page. */
  scope: HTMLElement | null;
  startIndex: number;
  language: HelpLanguage;
  /** "Point at a field" from the tour card: end the tour, start the inspector. */
  onInspect: () => void;
  onClose: () => void;
}

const PAD = 6;
const CARD_WIDTH = 360;
/** Smooth scroll keeps moving the target for a while; track it with rAF. */
const TRACK_FRAMES = 40;

export function HelpTour({ page, steps, scope, startIndex, language, onInspect, onClose }: Props) {
  const t = helpText(language);
  const [index, setIndex] = useState(startIndex);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [cardHeight, setCardHeight] = useState(180);
  const [jumpAnchor, setJumpAnchor] = useState<DOMRect | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const counterRef = useRef<HTMLButtonElement>(null);
  const directionRef = useRef<1 | -1>(1);
  // a step whose reveal already ran without success is skipped, not retried in a loop
  const revealTriedRef = useRef(new Set<HelpStep>());
  const [revealed, setRevealed] = useState(0);

  const step = steps[index];
  const groups = useMemo(() => groupStepsBySection(steps, page.sections), [steps, page.sections]);
  const groupIndex = step ? groups.findIndex((g) => g.steps.includes(step)) : -1;

  const go = useCallback(
    (next: number) => {
      if (next < 0) return;
      if (next >= steps.length) return onClose();
      directionRef.current = next >= index ? 1 : -1;
      setJumpAnchor(null);
      setIndex(next);
    },
    [index, steps.length, onClose]
  );

  const goSection = (delta: 1 | -1) => {
    const target = groups[groupIndex + delta]?.steps[0];
    if (target) go(steps.indexOf(target));
  };

  // Locate the step's element; re-open what it lives in (a menu closes on the
  // click on Next) or skip it if it vanished; track it while smooth scroll settles.
  useLayoutEffect(() => {
    if (!step) return onClose();
    const root: ParentNode = scope && scope.isConnected ? scope : document;
    if (scope && !scope.isConnected) return onClose(); // the dialog the tour was scoped to closed
    const el = findStepElement(step.selector, root);
    if (!el) {
      // re-open on this screen only: a tour never navigates away
      if (revealRecipe(page, step) && !revealTriedRef.current.has(step)) {
        revealTriedRef.current.add(step);
        let alive = true;
        void revealHelpStep(page, step).then(() => alive && setRevealed((n) => n + 1));
        return () => {
          alive = false;
        };
      }
      const next = index + directionRef.current;
      if (next < 0 || next >= steps.length) onClose();
      else setIndex(next);
      return;
    }
    revealTriedRef.current.delete(step);
    el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
    let frame = 0;
    let raf = 0;
    const measure = () => setRect(el.getBoundingClientRect());
    const tick = () => {
      measure();
      if (++frame < TRACK_FRAMES) raf = window.requestAnimationFrame(tick);
    };
    tick();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [step, scope, index, steps.length, onClose, page, revealed]);

  useLayoutEffect(() => {
    if (cardRef.current) setCardHeight(cardRef.current.offsetHeight);
  }, [step, rect, language]);

  // Capture phase: Escape must end the tour, not close the dialog underneath it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (jumpAnchor) {
        if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          setJumpAnchor(null);
          counterRef.current?.focus();
        }
        return; // arrows belong to the jump list's text caret
      }
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") go(index + 1);
      else if (e.key === "ArrowLeft") go(index - 1);
      else return;
      e.preventDefault();
      e.stopPropagation();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [go, index, onClose, jumpAnchor]);

  if (!step || !rect) return null;

  const text = localizedStep(step, language);
  const section = step.section ? localizedSection(page, step.section, language).value : null;
  const hole = {
    top: rect.top - PAD,
    left: rect.left - PAD,
    width: rect.width + 2 * PAD,
    height: rect.height + 2 * PAD,
  };
  const pos = cardPosition(
    { top: hole.top, bottom: hole.top + hole.height, left: hole.left, width: hole.width },
    { width: CARD_WIDTH, height: cardHeight }
  );

  return (
    <Portal>
      <div {...{ [HELP_PANEL_ATTR]: "tour" }}>
        {/* the scrim: four panels around the target, then the outline — plain divs render reliably */}
        {scrimPanels(hole).map((panel, i) => (
          <div
            key={i}
            aria-hidden
            style={{
              position: "fixed",
              ...panel,
              zIndex: Z.tourOverlay,
              backgroundColor: SCRIM,
              pointerEvents: "none",
            }}
          />
        ))}
        <div
          aria-hidden
          style={{
            position: "fixed",
            top: hole.top,
            left: hole.left,
            width: hole.width,
            height: hole.height,
            borderRadius: 6,
            outline: "2px solid #f2a900",
            zIndex: Z.tourOverlay,
            pointerEvents: "none",
          }}
        />
        <div
          ref={cardRef}
          role="dialog"
          aria-label={text.value.title}
          style={{
            position: "fixed",
            top: pos.top,
            left: pos.left,
            width: CARD_WIDTH,
            zIndex: Z.tourCard,
            padding: 16,
            borderRadius: 12,
            background: tokens.colorNeutralBackground1,
            color: tokens.colorNeutralForeground1,
            boxShadow: tokens.shadow28,
            fontSize: 14,
          }}
        >
          {/* header: step counter (opens the step index), section arrows, close */}
          <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
            <button
              ref={counterRef}
              type="button"
              data-help="help-tour-jump"
              aria-label={t("help.tour.jumpAria", "Step {{n}} of {{total}} — jump to another step", {
                n: index + 1,
                total: steps.length,
              })}
              onClick={(e) => setJumpAnchor(jumpAnchor ? null : e.currentTarget.getBoundingClientRect())}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                border: 0,
                padding: "2px 0",
                background: "transparent",
                cursor: "pointer",
                font: "inherit",
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 1.2,
                textTransform: "uppercase",
                color: tokens.colorBrandForeground1,
              }}
            >
              {t("help.tour.stepOf", "Step {{n}} of {{total}}", { n: index + 1, total: steps.length })}
              <ChevronDown12Regular />
            </button>
            <span style={{ flex: 1 }} />
            {!text.translated && <EnPill language={language} />}
            <Button
              size="small"
              appearance="transparent"
              icon={<ChevronDoubleLeft20Regular />}
              disabled={groupIndex <= 0}
              aria-label={t("help.tour.prevSection", "Previous section")}
              title={t("help.tour.prevSection", "Previous section")}
              onClick={() => goSection(-1)}
            />
            <Button
              size="small"
              appearance="transparent"
              icon={<ChevronDoubleRight20Regular />}
              disabled={groupIndex < 0 || groupIndex >= groups.length - 1}
              aria-label={t("help.tour.nextSection", "Next section")}
              title={t("help.tour.nextSection", "Next section")}
              onClick={() => goSection(1)}
            />
            <Button
              size="small"
              appearance="transparent"
              icon={<Dismiss20Regular />}
              aria-label={t("help.tour.close", "End tour")}
              onClick={onClose}
            />
          </div>
          {section && (
            <div
              style={{
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: 1.2,
                textTransform: "uppercase",
                color: tokens.colorNeutralForeground3,
                marginTop: 2,
              }}
            >
              {section}
            </div>
          )}
          <div style={{ fontWeight: 600, fontSize: 15, margin: "4px 0 8px" }}>{text.value.title}</div>
          <div style={{ fontSize: 13, lineHeight: 1.5, whiteSpace: "pre-line", color: tokens.colorNeutralForeground2 }}>
            {text.value.body}
          </div>
          {/* footer: switch to point-at-a-field, back, next */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 16 }}>
            <Button
              size="small"
              appearance="subtle"
              icon={<HandPoint20Regular />}
              data-help="help-tour-inspect"
              onClick={onInspect}
            >
              {t("help.drawer.inspect", "Point at a field")}
            </Button>
            <span style={{ flex: 1 }} />
            <Button size="small" appearance="subtle" disabled={index === 0} onClick={() => go(index - 1)}>
              {t("help.tour.back", "Back")}
            </Button>
            <Button size="small" appearance="primary" onClick={() => go(index + 1)}>
              {index === steps.length - 1 ? t("help.tour.done", "Done") : t("help.tour.next", "Next")}
            </Button>
          </div>
        </div>
        {jumpAnchor && (
          <HelpStepJump
            page={page}
            steps={steps}
            current={index}
            language={language}
            anchor={jumpAnchor}
            toggleRef={counterRef}
            onJump={go}
            onClose={() => setJumpAnchor(null)}
          />
        )}
      </div>
    </Portal>
  );
}

const SCRIM = "rgba(0, 0, 0, 0.45)";

type Box = { top: number; left: number; width: number; height: number };

/** Four rectangles covering the viewport except `hole`. */
function scrimPanels(hole: Box): Box[] {
  const right = hole.left + hole.width;
  const bottom = hole.top + hole.height;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  return [
    { top: 0, left: 0, width: vw, height: Math.max(0, hole.top) },
    { top: bottom, left: 0, width: vw, height: Math.max(0, vh - bottom) },
    { top: hole.top, left: 0, width: Math.max(0, hole.left), height: hole.height },
    { top: hole.top, left: right, width: Math.max(0, vw - right), height: hole.height },
  ];
}

export function EnPill({ language }: { language: HelpLanguage }) {
  const t = helpText(language);
  return (
    <span
      title={t("help.untranslated", "Not translated yet — shown in English")}
      style={{
        fontSize: 10,
        fontWeight: 600,
        padding: "1px 6px",
        borderRadius: 8,
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        color: tokens.colorNeutralForeground3,
      }}
    >
      EN
    </span>
  );
}
